import React, { useMemo } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

/**
 * Performance Line Chart Component
 * Displays portfolio value over time
 */
function PerformanceChart({ data, period }) {
  // Calculate min and max values for Y-axis domain
  const { minValue, maxValue, hasGain } = useMemo(() => {
    if (!data || data.length === 0) return { minValue: 0, maxValue: 0, hasGain: false };

    const values = data.map(d => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const firstValue = values[0];
    const lastValue = values[values.length - 1];

    return {
      minValue: min * 0.95, // Add 5% padding below
      maxValue: max * 1.05, // Add 5% padding above
      hasGain: lastValue >= firstValue
    };
  }, [data]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{formatDate(data.date)}</p>
          <p className="tooltip-value">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'EUR'
            }).format(data.value)}
          </p>
        </div>
      );
    }
    return null;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: data.length > 180 ? 'numeric' : undefined
    }).format(date);
  };

  // Format Y-axis values to currency
  const formatYAxis = (value) => {
    if (value >= 1000000) {
      return `€${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `€${(value / 1000).toFixed(0)}k`;
    }
    return `€${value}`;
  };

  // Determine tick interval based on data length
  const getTickInterval = () => {
    if (period === 365) return Math.floor(data.length / 6); // ~6 ticks for 1 year
    if (period === 90) return Math.floor(data.length / 5); // ~5 ticks for 90 days
    return Math.floor(data.length / 4); // ~4 ticks for 30 days
  };

  // Calculate performance metrics
  const performanceMetrics = useMemo(() => {
    if (!data || data.length < 2) return null;

    const firstValue = data[0].value;
    const lastValue = data[data.length - 1].value;
    const change = lastValue - firstValue;
    const changePercent = firstValue > 0 ? (change / firstValue) * 100 : 0;

    return {
      startValue: firstValue,
      endValue: lastValue,
      change,
      changePercent
    };
  }, [data]);

  // Early return after all hooks are defined
  if (!data || data.length === 0) {
    return (
      <div className="chart-empty-state">
        <p>No performance data available. Create snapshots to track performance over time.</p>
      </div>
    );
  }

  return (
    <div className="chart-container performance-chart-container">
      {/* Performance Summary */}
      {performanceMetrics && (
        <div className="performance-summary">
          <div className="performance-metric">
            <span className="metric-label">Start Value:</span>
            <span className="metric-value">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'EUR'
              }).format(performanceMetrics.startValue)}
            </span>
          </div>
          <div className="performance-metric">
            <span className="metric-label">Current Value:</span>
            <span className="metric-value">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'EUR'
              }).format(performanceMetrics.endValue)}
            </span>
          </div>
          <div className="performance-metric">
            <span className="metric-label">Change:</span>
            <span className={`metric-value ${performanceMetrics.change >= 0 ? 'positive' : 'negative'}`}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'EUR',
                signDisplay: 'always'
              }).format(performanceMetrics.change)}
              {' '}
              ({performanceMetrics.changePercent >= 0 ? '+' : ''}
              {performanceMetrics.changePercent.toFixed(2)}%)
            </span>
          </div>
        </div>
      )}

      {/* Area Chart */}
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          aria-label="Portfolio performance chart"
        >
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={hasGain ? '#10B981' : '#EF4444'}
                stopOpacity={0.3}
              />
              <stop
                offset="95%"
                stopColor={hasGain ? '#10B981' : '#EF4444'}
                stopOpacity={0}
              />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            interval={getTickInterval()}
            tick={{ fontSize: 12 }}
          />
          <YAxis
            tickFormatter={formatYAxis}
            domain={[minValue, maxValue]}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="value"
            stroke={hasGain ? '#10B981' : '#EF4444'}
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorValue)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default PerformanceChart;
